<template>
  <div class="social-verification-container">
    <div class="edit-form-title">
      <h1>Linked Account</h1>
    </div>
    <div class="social-text-note">
      Build trust within the community and discover your trusted
      connections all over the world. We take the security and privacy
      of our community very seriously. For information about privacy on YouPiared,
      please read our Privacy Policy.
      <hr/>
    </div>
    <div class="social-buttons">
      <div class="social-button-container"
        v-for="network in linkedNetworks" :key="network.id">
        <button :class="classForNetwork(network)" aria-label="Connect Social Media"
          @click="connectNetwork(network)">
          <font-awesome-icon :icon="['fab', network.icon]" size="2x"/>
          <span>{{network.verified ? 'Connected': `Connect with ${network.name}`}}</span>
        </button>
        <div class="verified-icon" v-if="network.verified">
          <font-awesome-icon :icon="['fa', 'check']" size="1x"/>
          <!-- <span>Linked</span> -->
        </div>
      </div>
    </div>
    <div>
      <div class="phone-container-text">
        <span>Add a trusted phone number</span>
        <div class="tooltip"><font-awesome-icon :icon="['fa', 'lock']" size="sm"
          :style="{ color: 'BBBBBB' }"/>
         <span class="tooltiptext">This information will remain private</span>
        </div>
      </div>
      <div class="phone-note">When you click "Verify", you will receive a code in a text message at
         the phone number you specified. Simply enter that code and get verified immediately!
      </div>
      <div class="phone-container">
        <select v-model="phoneCode" class="phone-country-code"
          :disabled="isPhoneVerified">
          <option disabled>Country</option>
          <option v-for="(regionCode, i) in regionCodes"
            :key="i" :value="regionCode.code">
            {{ `${regionCode.country} (+${regionCode.code})` }}
          </option>
        </select>
        <input type="tel" v-model="phoneNumber" class="phone-number"
          :disabled="isPhoneVerified" input="phone-number"/>
        <div class="verified-icon" v-if="isPhoneVerified">
          <font-awesome-icon :icon="['fa', 'check']" size="1x"/>
          <!-- <span>Linked</span> -->
        </div>
        <button
          v-else
          @click="verifyPhone" aria-label="Verify Phone"
          class="verify-phone-button"
          :disabled="showOTPInput">Verify Now</button>
        <input type="text" v-if="showOTPInput" @input="onOtpInput"
          class="otp-input" placeholder="6 digit code" :disabled="isVerifying"/>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'social-verification',
  data() {
    return {
      linkedNetworks: [],
      phone: '',
      regionCodes: [],
      showOTPInput: false,
      isPhoneVerified: false,
      isVerifying: false,
    };
  },
  async mounted() {
    const [phoneCountryCodes, verificationStatus] = await Promise.all([
      this.backendAxios.get('/account/phone-country-codes'),
      this.backendAxios.get('/account/verification_status'),
    ]);
    this.regionCodes = phoneCountryCodes.data;
    this.linkedNetworks = [];
    this.socialNetworks.forEach((network) => {
      const mutatedNetwork = Object.assign({}, network);
      const linkedNetwork = verificationStatus.data.linkedNetworks
        .find(({ connection }) => connection === network.id);
      if (linkedNetwork) {
        mutatedNetwork.verified = true;
        mutatedNetwork.link = linkedNetwork.link;
      }
      this.linkedNetworks.push(mutatedNetwork);
    });
    this.phone = verificationStatus.data.phone;
    this.isPhoneVerified = verificationStatus.data.isPhoneVerified;
  },
  computed: {
    ...mapState('auth', ['backendAxios', 'socialNetworks']),
    phoneCode: {
      get() {
        if (!this.phone) {
          return '';
        }
        return this.phone.split(' ')[0];
      },
      set(val) {
        this.phone = `${val} ${this.phoneNumber}`;
      },
    },
    phoneNumber: {
      get() {
        if (!this.phone) {
          return '';
        }
        return this.phone.split(' ')[1];
      },
      set(val) {
        this.phone = `${this.phoneCode} ${val}`;
      },
    },
  },
  methods: {
    classForNetwork({ id, verified }) {
      if (verified) {
        return `social-button social-button-verified-button ${id}-verified-icon`;
      }
      return `social-button ${id}-icon`;
    },
    connectNetwork(network) {
      if (!network.verified) {
        this.$ga.event('account', 'click', network.id, 1);
        window.location =
          `${process.env.VUE_APP_BACKEND_URL}/auth/${network.id}/state`;
      }
    },
    async verifyPhone() {
      try {
        await this.backendAxios.post('/validate/phoneNumber', {
          phoneNumber: this.phone,
        });
        await this.backendAxios.post('/account/add-phone-and-send-otp', {
          phone: this.phone,
        });
        this.showOTPInput = true;
      } catch (err) {
        this.$toasted.show(err.response.data, {
          type: 'info',
          duration: 5000,
        });
      }
    },
    async onOtpInput(e) {
      const otp = e.target.value;
      if (otp.length !== 6) {
        return;
      }
      this.isVerifying = true;
      try {
        await this.backendAxios.post('/account/verify-phone-otp', {
          otp,
        });
        this.showOTPInput = false;
        this.isPhoneVerified = true;
      } catch (err) {
        this.$toasted.show(err.response.data, {
          type: 'info',
          duration: 5000,
        });
      }
      this.isVerifying = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/youpaired-colors.scss';

.social-verification-container {
  padding: 0 0 2rem 0;
}

.social-verification-header {
  display: flex;
  justify-content: center;
  font-size: 1.25rem;
  font-weight: 500;
  line-height: 1rem;
  padding: 1rem;
  background-color: #E8e8E8;
}

.social-text-note {
  color: $text-note;
  font-size: 1rem;
  line-height: 1.5rem;
  padding: 1rem;
  text-align: justify;
}

.social-text-note hr {
  border: 0.05rem solid #e8e8e8;
  margin-top: 1rem;
}

.social-buttons {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  padding: 0rem 0 0 1rem;
  margin: 0.75rem 0;
}

.social-button {
  display: flex;
  cursor: pointer;
  width: 16rem;
  justify-content: left;
  align-items: center;
  color: #fff;
  &:hover {
    filter: brightness(1.2);
    -webkit-filter: brightness(1.2);
  }
  padding: 0 0 0 0.5rem;
  margin: 0.25rem 0 0.25rem 0;
  border-radius: 0.3rem;
}

.social-button-verified-button {
  border: none;
  background-color: white;
  cursor: auto;
  &:hover {
    filter: none;
    -webkit-filter: none;
  }
}

.social-button > span {
  padding: 0.5rem;
  font-size: 1rem;
  font-weight: 500;
  text-align: left;
}

.social-button-container {
  display: flex;
  align-items: center;
}

.verified-icon {
  color: green;
}

.facebook-icon {
  background-color: $facebook;
  border: 1px solid $facebook;
}

.facebook-verified-icon {
  color: $facebook;
}

.google-icon {
  background-color: $google;
  border: 1px solid $google;
}

.google-verified-icon {
  color: $google;
}

.twitter-icon {
  background-color: $twitter;
}

.twitter-verified-icon {
  color: $twitter;
}

.twitch-icon {
  background-color: $twitch;
  border: 1px solid $twitch;
}

.twitch-verified-icon {
  color: $twitch;
}

.linkedin-icon {
  background-color: $linkedin;
  border: 1px solid $linkedin;
}

.linkedin-verified-icon {
  color: $linkedin;
}

.phone-container {
  display: flex;
  align-items: center;
  padding: 0 0 0 1rem;
  margin: 0.75rem 0;
}

.phone-container-text{
  color: #484848;
  font-size: 1rem;
  font-weight: 600;
  padding: 1rem 1.5rem;
  text-align: start;
  border-top: 1px solid #dedbdb;
}

.phone-note {
  color: #484848;
  font-size: 1rem;
  line-height: 1.5rem;
  padding: 0 1.5rem;
  text-align: justify;
}

.phone-country-code {
  width: 5rem;
  font-size: 0.875rem;
  font-weight: normal;
  color: #484848;
  background-color: white;
  height: 2.2rem;
  border: 0.05rem solid #DCDBDB;
  border-radius: 0.1rem;
  padding: 0 1rem 0 0;
}

.phone-number {
  width: 10rem;
  margin: 0 0.3rem 0 0.5rem;
  text-indent: 0.3rem;
  text-align: left;
  font-size: 0.875rem;
  font-weight: normal;
  color: #484848;
  background-color: white;
  height: 2rem;
  border: 0.05rem solid #DCDBDB;
  border-radius: 0.1rem;
}

.verify-phone-button {
  cursor: pointer;
  width: 7rem;
  padding: 0.5rem;
  font-size: 1rem;
  color: #FFF;
  font-weight: 800;
  background-color: $verify-phone;
  border-color: $verify-phone;
   &:hover{
    background-color: $login-hover;
    border:none;
  }
  margin: 0 0 0 1rem;
  border-radius: 0.2rem;
}

.otp-input {
  margin: 0 0 0 1rem;
  width: 6rem;
  height: 1.5rem;
  font-size: 0.875rem;
}

.trusted-text-lock-icon {
  margin: 0 0 0 0.5rem;
}

.tooltip {
  position: relative;
  display: inline-block;
}

.tooltip > svg {
  border-bottom: 1px dotted #444444;
}

@media screen and (max-width: 375px) {
  .tooltip {
    width: 90%;
  }
}

.tooltip .tooltiptext {
  visibility: hidden;
  height: auto;
  width: 12rem;
  background-color: #e4e4e4;
  color: #444444;
  text-align: left;
  border-radius: 6px;
  padding: 10px;
  font-size: 0.75rem;
  position: absolute;
  z-index: 1;
  bottom: 0;
  left: 0;
  right: 0;
}

.tooltip .tooltiptext::after {
  content: "";
  position: absolute;
}

.tooltip:hover .tooltiptext {
  visibility: visible;
}

</style>
